import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface Pro {
  id: number;
  name: string;
  main_team_id: number;
  is_private: boolean;
  email?: string;
  phone?: string;
  next_job?: {
    date: string;
    start_time: string;
  };
  last_job?: {
    date: string;
    start_time: string;
  };
  address_homekeeper_priorities?: AddressHomekeeperPriority[];
  homekeeper?: {
    id: number;
    name: string;
    email?: string;
    phone?: string;
  };
  [key: string]: any;
}

export interface AddressHomekeeperPriority {
  id: number;
  address_id: number;
  service_type_id: number;
  priority: number;
  object_type: string;
  object?: {
    id: number;
    first_name: string;
    is_private: boolean;
  };
  ignore_homekeeper_availability?: boolean;
}

export interface ProDetail {
  homekeeper: {
    id: number;
    name: string;
    email?: string;
    phone?: string;
  };
  [key: string]: any;
}

export interface BlacklistedPro {
  id: number;
  homekeeper: {
    id: number;
    name: string;
  };
  [key: string]: any;
}

@Injectable({ providedIn: 'root' })
export class ProsApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.api_url;

  getAllPros(): Observable<Pro[]> {
    const url = `${this.baseUrl}api/v1/customer/homekeepers?includes=next_job,last_job,address_homekeeper_priorities`;
    return this.http.get<Pro[]>(url);
  }

  getAllProsBasic(): Observable<Pro[]> {
    const url = `${this.baseUrl}api/v1/customer/homekeepers`;
    return this.http.get<Pro[]>(url);
  }

  getAllProsByAddressFilter(addressId: number): Observable<Pro[]> {
    const url = `${this.baseUrl}api/v1/customer/homekeepers?filters[address_ids]=${addressId}`;
    return this.http.get<Pro[]>(url);
  }

  getAllProsWithPriorities(): Observable<Pro[]> {
    const url = `${this.baseUrl}api/v1/customer/homekeepers?includes=address_homekeeper_priorities`;
    return this.http.get<Pro[]>(url);
  }

  getProsExcludingAddress(addressId: number): Observable<Pro[]> {
    const url = `${this.baseUrl}api/v1/customer/homekeepers?exclude_homeekepers_on_address_id=${addressId}`;
    return this.http.get<Pro[]>(url);
  }

  getProDetail(proId: number, addressId: number): Observable<ProDetail> {
    const url = `${this.baseUrl}api/v1/customer/homekeepers/${proId}/addresses/${addressId}/details-page`;
    return this.http.get<ProDetail>(url);
  }

  getBlacklistedPros(): Observable<BlacklistedPro[]> {
    const url = `${this.baseUrl}api/v1/customer/blacklisted-homekeepers`;
    return this.http.get<BlacklistedPro[]>(url);
  }

  blacklistPro(proIds: number[]): Observable<any> {
    const url = `${this.baseUrl}api/v1/customer/blacklisted-homekeepers`;
    return this.http.post(url, { homekeeper_ids: proIds });
  }

  unblacklistPro(proId: number): Observable<any> {
    const url = `${this.baseUrl}api/v1/customer/blacklisted-homekeepers/${proId}/inactivate`;
    return this.http.post(url, {});
  }

  addNewPro(params: any): Observable<Pro> {
    const url = `${this.baseUrl}api/v1/customer/homekeepers`;
    return this.http.post<Pro>(url, params);
  }

  updateProInfo(proId: number, profile: Partial<{ name: string; email: string; phone: string }>): Observable<Pro> {
    const url = `${this.baseUrl}api/v1/customer/homekeepers/${proId}`;
    const data = { homekeeper_id: proId, profile };
    return this.http.put<Pro>(url, data);
  }

  addExistingPros(params: any, addressId: number): Observable<any> {
    const url = `${this.baseUrl}api/v1/customer/addresses/${addressId}/homekeeper-list/add-homekeepers`;
    return this.http.post(url, params);
  }

  removeProFromAddressList(addressId: number, payload: any): Observable<any> {
    const url = `${this.baseUrl}api/v1/customer/addresses/${addressId}/homekeeper-list/inactivate-homekeepers`;
    return this.http.put(url, payload);
  }

  createManyPros(payload: any): Observable<any> {
    const url = `${this.baseUrl}api/v1/customer/homekeepers/create-many`;
    return this.http.post(url, payload);
  }
}
